import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import TabButtons from '@/components/atoms/TabButtons';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import appRoutes from '@/constants/routes/app';
import useApproveCreatorApplication from '@/hooks/creator-applications/useApproveCreatorApplication';
import useCreatorApplicationById from '@/hooks/creator-applications/useCreatorApplicationById';
import useRejectCreatorApplication from '@/hooks/creator-applications/useRejectCreatorApplication';
import RejectApplicationModal from '@/modules/creator-application/RejectApplicationModal';
import DetailsTab from '@/modules/creator-application/tabs/DetailsTab';
import HistoryTab from '@/modules/creator-application/tabs/HistoryTab';
import ProfileTab from '@/modules/creator-application/tabs/ProfileTab';
import colors from '@/theme/colors';
import {
  CREATOR_APPLICANT_TYPES,
  CREATOR_APPLICATION_PAGE_SIZE_OPTIONS,
  CREATOR_APPLICATION_STATUSES,
  TCreatorApplicantType,
  TCreatorApplicationPageSize,
  TCreatorApplicationStatus,
} from '@/types/CreatorApplication.types';
import { Center, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useState } from 'react';

type TabOption = 'details' | 'profile' | 'history';
type CreatorApplicationsBackSearch = {
  status?: TCreatorApplicationStatus;
  q?: string;
  applicantType?: TCreatorApplicantType;
  sort?: string;
  pageSize?: TCreatorApplicationPageSize;
};

const SORT_PARAM_REGEX = /^submittedAt:(asc|desc)$/;

const CreatorApplicationDetail = () => {
  const { applicationId } = Route.useParams();
  const listSearch = Route.useSearch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabOption>('details');

  const approveDisclosure = useDisclosure();
  const rejectDisclosure = useDisclosure();

  const { isLoading, isPending, isRefetching, isSuccess, data } = useCreatorApplicationById(applicationId);
  const { isPending: isApproving, mutate: approve } = useApproveCreatorApplication(
    applicationId,
    approveDisclosure.onClose,
  );
  const { isPending: isRejecting, mutate: reject } = useRejectCreatorApplication(
    applicationId,
    rejectDisclosure.onClose,
  );

  const showLoader = isLoading || isPending || isRefetching;
  const isActionable = data?.status === 'pending';

  // Post-approval behavior differs by applicant type, so the confirmation copy does too.
  const approveDescription =
    data?.applicantType === 'external_email'
      ? 'This applicant has no platform account yet. Approving emails them a one-time link to set up their account — no account or Content Creator role is granted until they complete setup.'
      : 'Approving grants the Content Creator role immediately and notifies the applicant.';

  const actionButtons = isActionable ? (
    <HStack gap="20px" maxHeight={'40px'}>
      <CustomButton
        label="Reject"
        backgroundColor="extra.red"
        labelColor="extra.white"
        labelHoverColor="extra.white"
        onClick={rejectDisclosure.onOpen}
      />
      <CustomButton
        label="Approve"
        backgroundColor="extra.teal"
        labelColor="extra.white"
        labelHoverColor="extra.white"
        onClick={approveDisclosure.onOpen}
      />
    </HStack>
  ) : undefined;

  return (
    <SidebarItemViewTemplate title="Creator Application" customButton={actionButtons}>
      <HStack justify="flex-start" width="100%" marginTop="20px">
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIconPaths.leftArrow}
          labelColor={colors.background.blueGray}
          labelHoverColor="extra.black"
          label="Back to Creator Applications"
          hoverBackgroundColor="transparent"
          borderHoverColor="transparent"
          onClick={() => navigate({ to: appRoutes.creatorApplications, search: listSearch })}
          labelStyle={{ marginBottom: '5px', fontSize: '16px' }}
        />
      </HStack>

      <VStack flex={1} width="100%" align="center">
        <TabButtons
          options={[
            { onClick: () => setActiveTab('details'), labelValue: 'details', labelDisplay: 'Details' },
            { onClick: () => setActiveTab('profile'), labelValue: 'profile', labelDisplay: 'Profile' },
            {
              onClick: () => setActiveTab('history'),
              labelValue: 'history',
              labelDisplay: 'Application History',
            },
          ]}
          selectedOptionValue={activeTab}
        />

        <VStack align="stretch" width="100%" maxWidth="760px" marginTop="20px" marginBottom="40px">
          {showLoader && (
            <Center width="100%" paddingY="40px">
              <LoadingLogo />
            </Center>
          )}
          {!showLoader && isSuccess && data && (
            <>
              {activeTab === 'details' && <DetailsTab data={data} />}
              {activeTab === 'profile' && <ProfileTab profile={data.profile} />}
              {activeTab === 'history' && <HistoryTab history={data.history} />}
            </>
          )}
        </VStack>
      </VStack>

      <ConfirmationModal
        isOpen={approveDisclosure.isOpen}
        title="Approve Application"
        description={approveDescription}
        confirmButtonText="Approve"
        confirmButtonColor="extra.teal"
        confirmLoading={isApproving}
        onCancel={approveDisclosure.onClose}
        onConfirm={() => approve()}
      />

      <RejectApplicationModal
        isOpen={rejectDisclosure.isOpen}
        isLoading={isRejecting}
        onCancel={rejectDisclosure.onClose}
        onConfirm={(decisionNotes) => reject(decisionNotes)}
      />
    </SidebarItemViewTemplate>
  );
};

export default CreatorApplicationDetail;

export const Route = createFileRoute('/_authenticated/_admin/creator-applications/$applicationId')({
  component: CreatorApplicationDetail,
  validateSearch: (search: Record<string, unknown>): CreatorApplicationsBackSearch => {
    const result: CreatorApplicationsBackSearch = {};

    if (CREATOR_APPLICATION_STATUSES.includes(search.status as TCreatorApplicationStatus)) {
      result.status = search.status as TCreatorApplicationStatus;
    }

    if (CREATOR_APPLICANT_TYPES.includes(search.applicantType as TCreatorApplicantType)) {
      result.applicantType = search.applicantType as TCreatorApplicantType;
    }

    const rawQ = typeof search.q === 'string' ? search.q.trim() : '';
    if (rawQ) {
      result.q = rawQ;
    }

    if (typeof search.sort === 'string' && SORT_PARAM_REGEX.test(search.sort)) {
      result.sort = search.sort;
    }

    const parsedPageSize = Number(search.pageSize);
    if (CREATOR_APPLICATION_PAGE_SIZE_OPTIONS.includes(parsedPageSize as TCreatorApplicationPageSize)) {
      result.pageSize = parsedPageSize as TCreatorApplicationPageSize;
    }

    return result;
  },
});
